import {AfterContentInit, Component, isDevMode, OnDestroy, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { Location } from '@angular/common';
import {Title} from '@angular/platform-browser';

import {map, take} from 'rxjs';
import { AnimationOptions } from 'ngx-lottie';
import { environment } from '@src/environments/environment';
import { get } from 'lodash';

import {SurveyDisplayService} from "@survey/services/survey-display.service";

import {WebAccessService} from '@commons/services/web.access/web-access.service';
import {NotificationService} from '@commons/services/notification/notification.service';
import {PanelNavigationService} from '@commons/services/navigation/panel-navigation.service';
import {VehicleService} from '@commons/services/vehicle/vehicle.service';
import {EventService} from '@commons/services/event/event.service';
import {MapEventsService} from "@commons/services/map/map-events.service";

import packageJson from "../../../../package.json";

import {MapPanelService} from "@map-panel/services/map-panel.service";

import {NewNavigationService} from '@shared/services/new-navigation.service';
import {BaseComponent} from "@shared/components/base-component";
import {ErrorLoggerProxy} from "@shared/error-logger-proxy";
import {LogInApiHttpService} from "@shared/services-http/log-in-api-http.service";
import {PermissionService} from "@shared/services/permission.service";
import {AuthenticationService} from "@shared/services/authentication/authentication.service";
import {UserHttpService} from "@shared/services-http/user-http.service";
import {AppInitializationService} from "@shared/services/app-initialization.service";
import {NewWebSocketHttpService} from "@shared/websocket/new-web-socket-http.service";
import {
  LoadingInitialDataComponent
} from "@layout/panel/components/loading-initial-data/loading-initial-data.component";
import {DialogService} from "@shared/services/dialog.service";
import {MatDialogRef} from "@angular/material/dialog";
import { MapActionType } from '@src/app/enum/map.enum';
import { etollBalance } from '@layout/vehicle/http-services/responses/getETollBalance.response';
import { AdType } from '@src/app/enum/ad-type.enum';
import { AdsControlService } from '@shared/services/ads-control.service';
import { TawkToChatService } from '@shared/services/tawk-to-chat.service';
import { LOCAL_STORAGE_VAR } from '@src/app/utils/localstorage';
import { IDropdownMenuOptions } from '@src/app/interfaces/dropdown-menu.interface';
import { LanguageService } from '@shared/services/language.service';
import { Driver } from '@commons/models/domain/Driver';

@Component({
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class PanelComponent extends BaseComponent implements AfterContentInit, OnDestroy {

  readonly version: string = packageJson.version;
  public readonly AD_TYPE = AdType;

  public readonly isChristmasEvent: boolean = environment.isChristmasEvent;
  public readonly chrismatHatConfigLottie: AnimationOptions = {
    path: "/assets/lottie/christmas-hat.json"
  };

  isMapPanelVisible = false;
  unreadEventsCount = 0;
  visibleSegments?: number;

  hasPermissionToWorkers = false;
  hasPermissionToVehicles = false;
  hasPermissionToVehiclesList = false;
  hasPermissionToEvents = false;
  hasAccessToDataSharing = false;
  hasAccessToAreas = false;
  hasAccessToHandover = false;
  hasPermissionToCharts = false;
  hasPermissionToReports = false;
  hasPermissionToFilesDDD = false;

  isVehiclesHovered = false;
  isEventsHovered = false;
  isWorkerHovered = false;
  isHelpHovered = false;
  isDataSharingHovered = false;
  isAreaHovered = false;
  isHandoverHovered = false;

  public isVisibleAreasModule: boolean;
  public isVisibleNewsModule: boolean;
  public selectedEtollBalance: etollBalance;

  public directoriesDropdownOptions: IDropdownMenuOptions[] = [
    {img: '<i class="pi pi-user"></i>', title: 'Pracownicy', onClick: () => this.onOpenWorkerPanel() },
    // {img: '<img style="height: 11px" src="/assets/images/panel/i-vehicle-selected.svg"/>', title: 'Pojazdy', onClick: () => alert('Pojazdy') },
    // {img: '<img style="height: 11px" src="/assets/images/panel/groups.svg"/>', title: 'Grupy', onClick: () => alert('Grupy') }
  ];
  public tachoModulesDropdownOptions: IDropdownMenuOptions[] = [
    {img: '<img style="height: 11px" src="/assets/img/tachograph-icon-white.svg"/>', title: 'tachograph', onClick: () => this.onOpenTachographStatus() },
    {img: '<img style="height: 11px" src="/assets/img/tachograph-icon-white.svg"/>', title: 'tachographChart', onClick: () => this.onOpenTachographChart() },
    // {img: '<img style="height: 11px" src="/assets/img/filesddd.svg"/>', title: 'filesDDD', onClick: () => this.onOpenFilesDDD() },
  ];

  private currentNavInfo: any;
  private isInitialized = false;
  private loadingInitialDataComponentRef?: MatDialogRef<any, any>;

  get isLoggedByLink(): boolean {
    return this.authenticationService.isLoggedByToken ?? false;
  }

  // public isVehicleFullListOpened: boolean;

  get userInitials(): string {
    const result = this.userHttpService.userViewModel?.initials ?? '';
    return result;
  }

  get isVehiclesButtonActive(): boolean {
    return this.isVehiclePanelOpened || this.isVehiclesHovered;
  }

  get isEventsButtonActive(): boolean {
    return this.isEventsPanelOpened || this.isEventsHovered;
  }

  get isWorkerButtonActive(): boolean {
    return this.isWorkerPanelOpened || this.isWorkerHovered;
  }

  get isDataSharingButtonActive(): boolean {
    return this.isDataSharingPanelOpened || this.isDataSharingHovered;
  }

  get isHelpButtonActive(): boolean {
    return this.isHelpPanelOpened || this.isHelpHovered;
  }

  get isAreaButtonActive(): boolean {
    return this.isAreasPanelOpened || this.isAreaHovered;
  }

  get isHandoverButtonActive(): boolean {
    return this.isHandoverPanelOpened || this.isHandoverHovered;
  }

  get isVehiclePanelOpened(): boolean {
    return this.isCurrentActive('vehicle');
  }

  get isWorkerPanelOpened(): boolean {
    return this.isCurrentActive('workers');
  }

  get isCarParameterChartViewOpened(): boolean {
    return this.isCurrentActive('car-parameter-chart');
  }

  get isFilesDDDViewOpened(): boolean {
    return this.isCurrentActive('files-ddd');
  }

  get isTachographChartOpened(): boolean {
    return this.isCurrentActive('tachograph-charts');
  }

  get isTachographStatusOpened(): boolean {
    return this.isCurrentActive('tachograph');
  }

  get isTachographPermissionDeniedOpened(): boolean {
    return this.isCurrentActive('tachograph-permissions-denied');
  }

  get isRouteReportsViewOpened(): boolean {
    return this.isCurrentActive('routes-report');
  }

  get isWorkerAddFormPanelOpened(): boolean {
    return this.isCurrentActive('worker-form');
  }

  get isEventsPanelOpened(): boolean {
    return this.isCurrentActive('event');
  }

  get isHelpPanelOpened(): boolean {
    return this.isCurrentActive('help');
  }

  get isProfilePanelOpened(): boolean {
    return this.isCurrentActive('profile');
  }

  get isDataSharingPanelOpened(): boolean {
    return this.isCurrentActive('data-sharing');
  }

  get isAreasPanelOpened(): boolean {
    return this.isCurrentActive('area');
  }

  get isHandoverPanelOpened(): boolean {
    return this.isCurrentActive('handover');
  }

  get isVehicleFullListOpened(): boolean {
    return this.isCurrentActive('vehicle-full-list');
  }

  constructor(
    private navigationService: PanelNavigationService,
    private notificationService: NotificationService,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute,
    private vehicleService: VehicleService,
    private router: Router,
    private webAccessService: WebAccessService,
    private userHttpService: UserHttpService,
    private surveyDisplayService: SurveyDisplayService,
    public newNavigationService: NewNavigationService,
    private logInApiHttpService: LogInApiHttpService,
    private titleService: Title,
    private permissionService: PermissionService,
    private authenticationService: AuthenticationService,
    private mapPanelService: MapPanelService,
    private appInitializationService: AppInitializationService,
    private mapEventsService: MapEventsService,
    private newWebSocketHttpService: NewWebSocketHttpService,
    private dialogService: DialogService,
    public adControlService: AdsControlService,
    public tawkToChatService: TawkToChatService,
    public languageService: LanguageService,
  ) {
    super();
    this.logErrorInApi();
    this.authenticationService.startKeepSessionInterval();

    if (!this.isLoggedByLink) {
      this.subscribeToParamMap();
      this.subscribeToQueryParamMap();
    }

    this.setControlsVisibility();

    this.loadingInitialDataComponentRef = this.dialogService.open(LoadingInitialDataComponent);
  }

  ngOnDestroy() {
    this.unsubscribeAll();
    this.newWebSocketHttpService.close();
  }

  ngAfterContentInit(): void {
    this.subToMapPanelVisibility();
    this.afterAuthenticationFinished();
    this.refreshUrlParams();
    this.surveyDisplayService.tryShowSurvey();
    this.checkMonthlyTask();
  }

  public onSelectedBalance(data: etollBalance): void {
    this.selectedEtollBalance = data;
  }

  public onChangeMapModule(mapModule: MapActionType): void {
    if (mapModule === MapActionType.Areas) {
      this.isVisibleAreasModule = true;
      this.hideSwitchMapPanelOnChangeModule();
    }
  }

  public hideAreasModule(): void {
    this.isVisibleAreasModule = false;
  }

  public hideNewsModule(): void {
    this.isVisibleNewsModule = false;
  }

  isCurrentActive(subsite: string): boolean {
    const params = this.activatedRoute.snapshot.paramMap;
    return (params.get('subsite') === subsite);
  }

  private hideRightPanelsWhenOpenLeftPanels(hideChat?: boolean): void {
    this.hideAreasModule();
    this.hideNewsModule();
    if (hideChat) {
      this.tawkToChatService.hideChat();
    }
  }

  public onClickAdBaner(ad: AdType): void {
    if (ad === AdType.DriverLicense) {
      if (!this.hasPermissionToWorkers) {
        alert('Brak dostępu do podanego modułu');
        return;
      }
      this.onOpenWorkerPanel();
    }
  }

  onOpenVehiclesPanel() {
    if (this.isVehiclePanelOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toVehiclesList();
      this.hideRightPanelsWhenOpenLeftPanels();
  }

  onOpenEventsPanel() {
    if (this.isEventsPanelOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toEventsList();
      this.markEventsAsRead();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onBackFromWorkerForm(): void {
    this.newNavigationService.toWorkersList();
  }

  onOpenWorkerForm(data: Driver) {
    this.newNavigationService.toWorkerForm(data);
  }

  onOpenWorkerPanel() {
    if (this.isWorkerPanelOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toWorkersList();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenHandoverPanel() {
    this.hideSwitchMapPanelOnChangeModule();
    if (this.isHandoverPanelOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toHandover();
      this.hideRightPanelsWhenOpenLeftPanels(true);
  }

  onOpenCarParameterChart() {
    if (this.isCarParameterChartViewOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toCarParameterChartView();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenFilesDDD() {
    if (!this.hasPermissionToFilesDDD) {
      this.onClickTachographWithoutPermission();
      return;
    }
    if (this.isFilesDDDViewOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toFilesDDDView();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenTachographChart() {
    if (!this.hasPermissionToFilesDDD) {
      this.onClickTachographWithoutPermission();
      return;
    }
    if (this.isTachographChartOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toTachographView();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenTachographStatus() {
    if (!this.hasPermissionToFilesDDD) {
      this.onClickTachographWithoutPermission();
      return;
    }
    if (this.isTachographStatusOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toTachographStatusView();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onClickTachographWithoutPermission() {
    if (this.isTachographPermissionDeniedOpened || this.hasPermissionToFilesDDD)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toTachographPermissionDeniedView();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenRouteReports() {
    if (this.isRouteReportsViewOpened)
      this.newNavigationService.toMainPanel();
    else {
      this.newNavigationService.toReports();
      this.hideRightPanelsWhenOpenLeftPanels();
    }
  }

  onOpenDataSharingPanel() {
    this.hideSwitchMapPanelOnChangeModule();
    if (this.isDataSharingPanelOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toDataSharing();
      this.hideRightPanelsWhenOpenLeftPanels(true);
  }

  onShowHelp() {
    if (this.isHelpPanelOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toHelp();
    this.hideRightPanelsWhenOpenLeftPanels(true);
  }

  onShowProfile() {
    if (this.isProfilePanelOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toProfile();
    this.hideRightPanelsWhenOpenLeftPanels(true);
  }

  onButtonMouseOver(elementName: string) {
    this.setHoveredState(elementName, true);
  }

  onButtonMouseOut(elementName: string) {
    this.setHoveredState(elementName, false);
  }

  onOpenTekomWebsite() {
    window.open('https://tekom.pl/', '_blank')!.focus();
  }

  public onClickVehicleFullListModule(): void {
    if (this.isVehicleFullListOpened)
      this.newNavigationService.toMainPanel();
    else
      this.newNavigationService.toVehicleFullList();
      this.hideRightPanelsWhenOpenLeftPanels(true);
  }

  public showNewsPanel(): void {
    this.isVisibleNewsModule = !this.isVisibleNewsModule;
    this.tawkToChatService.hideChat();
    this.newNavigationService.toMainPanel();
  }

  public showChat(): void {
    this.newNavigationService.toMainPanel();
    this.isVisibleNewsModule = false;
    this.tawkToChatService.showWidgetAndChat();
  }

  private hideSwitchMapPanelOnChangeModule(): void {
    this.mapPanelService.isMapPanelVisibleSubject.next(false);
  }

  private logErrorInApi() {
    ErrorLoggerProxy.$onError.subscribe(error => {
      this.logInApiHttpService.logError(error).subscribe();
    });
  }

  private setHoveredState(elementName: string, state: boolean) {
    switch (elementName) {
      case 'vehicle':
        this.isVehiclesHovered = state;
        break;

      case 'event':
        this.isEventsHovered = state;
        break;

      case 'driver':
        this.isWorkerHovered = state;
        break;

      case 'data-sharing':
        this.isDataSharingHovered = state;
        break;

      case 'help':
        this.isHelpHovered = state;
        break;

      case 'area':
        this.isAreaHovered = state;
        break;

      case 'handover':
        this.isHandoverHovered = state;
        break;
    }
  }

  private markEventsAsRead() {
    this.eventService.resetEventNotification().subscribe(() => {
      this.unreadEventsCount = 0;
    });
  }

  private vehicleOnMapClicked(vehicleId: number) {
    // jak nie ma zadnej listy otwartej (ani lite ani full)
    // to otwieramy lite
    const item = this.navigationService.prepareMessage(this.currentNavInfo);

    const allClosed = item.subsites
      && item.subsites.indexOf('vehicle-lite') === -1
      && item.subsites.indexOf('vehicle-details') === -1
      && item.subsites.indexOf('vehicle-details-only') === -1
      && item.subsites.indexOf('vehicle-full') === -1;

    const isLiteOpen = item.subsites
      && (item.subsites.indexOf('vehicle-lite') !== -1
        || item.subsites.indexOf('vehicle-details') !== -1
        || item.subsites.indexOf('vehicle-details-only') !== -1
      );

    const isFullOpen = item.subsites && item.subsites.indexOf('vehicle-full') !== -1;

    if (allClosed || isLiteOpen) {
      this.router.navigate(['panel', 'vehicle'], {queryParams: {'mode': 'details', 'id': vehicleId}, queryParamsHandling: 'merge' });
    } else if (isFullOpen) {
      this.router.navigate(['panel', 'vehicle'], {queryParams: {'mode': 'full', 'id': vehicleId}, queryParamsHandling: 'merge' });
    }

    this.vehicleService.setFollowVehicleStatus(true);
  }

  private subscriptionsAfterInitialDataLoaded(isLoggedByLink: boolean) {
    this.addToUnsubscribe(
      this.mapEventsService.vehicleClicked.subscribe(vehicleId => {
        this.vehicleOnMapClicked(vehicleId);
      })
    );

    this.addToUnsubscribe(
      //TODO GB wczytywanie samych pojazdów działa tylko dla logowania się za pomocą linku
      this.appInitializationService.initialize(isLoggedByLink).subscribe(() => {
        this.subToEventsCountChange();
        this.refreshUrlParams(); //TODO GB po co to jest ?
        this.loadingInitialDataComponentRef?.close();
      })
    );
  }

  private subToEventsCountChange() {
    this.addToUnsubscribe(
      this.newWebSocketHttpService.eventNotification.subscribe(event => {
        if (event) {
          this.notificationService.notify(event);
          if (!this.isCurrentActive('event')) {
            this.unreadEventsCount += 1;
          }
        }
      }));
  }

  private setUnreadEventsNum() {
    this.addToUnsubscribe(
      this.webAccessService.GetEventNotification()
      .pipe(
        take(1),
        map(response => <any>response.Result))
      .subscribe(response => {
        if (!this.isCurrentActive('event')) {
          const count = get(response, 'Result', (response || 0));
          this.unreadEventsCount = count;
        }
      }));
  }

  private setControlsVisibility() {
    this.addToUnsubscribe(this.navigationService.getVisibilitySubject().subscribe(result => {
      if (result.subsites)
        this.setVisibleSegment(result.subsites);
    }));
  }

  private subscribeToParamMap() {
    this.activatedRoute.paramMap.subscribe(params => {
      const queryParams = this.activatedRoute.snapshot.queryParamMap;
      this.navigate(params, queryParams);
      this.setVisibleSegment([this.activatedRoute.snapshot.url.join('-')]);

      if (this.isCurrentActive('event'))
        this.unreadEventsCount = 0;
    });
  }

  private subscribeToQueryParamMap() {
    this.activatedRoute.queryParamMap.subscribe(queryParams => {
      const params = this.activatedRoute.snapshot.paramMap;
      this.navigate(params, queryParams);

      if (this.isCurrentActive('event'))
        this.unreadEventsCount = 0;
    });
  }

  /**
   * Send url params once again
   */
  private refreshUrlParams() {
    const params = this.activatedRoute.snapshot.paramMap;
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    this.navigate(params, queryParams);
  }

  private navigate(url: any, queryParams: any) {
    const change = {
      url: url,
      query: queryParams
    };
    this.currentNavInfo = change;
    this.navigationService.resolve(change);
  }

  private isEventsPermitted(): boolean {
    if (!this.hasPermissionToVehicles) {
      return false;
    }
    return this.permissionService.hasAlarms_RAZ_PO()
      || this.permissionService.hasStdReports_FUELALL_POL()
      || this.permissionService.hasStdReports_ROK_SP();
  }

  private setVisibleSegment(subsites: string[]) {
    if (subsites.indexOf('driver-lite') !== -1) {
      this.visibleSegments = 1;

    } else if (subsites.indexOf('driver-details') !== -1) {
      this.visibleSegments = 2;

    } else if (subsites.indexOf('vehicle-lite') !== -1) {
      this.visibleSegments = 1;

    } else if (subsites.indexOf('vehicle-details') !== -1) {
      this.visibleSegments = 2;

    } else if (subsites.indexOf('vehicle-full') !== -1) {
      this.visibleSegments = 3;

    } else if (subsites.indexOf('vehicle-details-only') !== -1) {
      this.visibleSegments = 1;

    } else if (subsites.indexOf('event-lite') !== -1) {
      this.visibleSegments = 1;

    } else if (subsites.indexOf('area-lite') !== -1) {
      this.visibleSegments = 1;

    } else {
      this.visibleSegments = 0;
    }
  }

  private afterAuthenticationFinished() {
    this.authenticationService.isAuthenticated$.pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated && !this.isInitialized) {
        this.isInitialized = true;
        this.titleService.setTitle('MyCar - ' + this.authenticationService.currentClient);
        this.updateClientIdInRouteQueryParams();

        if (!this.isLoggedByLink) {
          this.addToUnsubscribe(this.userHttpService.getProfileUserData().subscribe());
          this.setPermissionsToPanelOptions();
          this.setUnreadEventsNum();
        }
        this.subscriptionsAfterInitialDataLoaded(this.authenticationService.isLoggedByToken);
      }
    });
  }

  private updateClientIdInRouteQueryParams(): void {
    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams: { client: this.authenticationService.currentClient },
        queryParamsHandling: 'merge',
      }
    );
  }

  private setPermissionsToPanelOptions() {
    this.hasPermissionToWorkers = this.permissionService.hasCardFiles_FPersonals();
    this.hasPermissionToVehicles = this.permissionService.hasStatus_FormStatusMap();
    this.hasPermissionToVehiclesList = this.permissionService.hasStatus_FormStatusTab();
    this.hasAccessToDataSharing = this.permissionService.hasDataSharing();
    this.hasPermissionToEvents = this.isEventsPermitted();
    this.hasAccessToAreas = this.isAreasManagementPermitted();
    this.hasAccessToHandover = !!this.authenticationService.currentClient;
    this.hasPermissionToCharts = this.permissionService.hasAnalisys_ASP_PO();
    this.hasPermissionToReports = this.permissionService.hasVehicleRoutes();
    this.hasPermissionToFilesDDD = this.permissionService.hasDriverTachoFiles() || this.permissionService.hasVehicleTachoFiles();
  }

  private subToMapPanelVisibility() {
    this.addToUnsubscribe(
      this.mapPanelService.isMapPanelVisibleSubject.subscribe(isVisible => {
        this.isMapPanelVisible = isVisible;
        if (isVisible) {
          this.hideAreasModule();
          this.hideNewsModule();
        }
      }));
  }

  private isAreasManagementPermitted(): boolean {
    const hasModuleAreasManagement = this.permissionService.hasAreasManagement();
    const hasTrackAreasFormClientAreasMap = this.permissionService.hasTrackAreasFormClientAreasMap();
    return hasModuleAreasManagement && hasTrackAreasFormClientAreasMap;
  }

  /*Pokazanie newsow jezel minal miesiac*/
  public checkMonthlyTask(): void {
    const lastRunDateStr = localStorage.getItem(LOCAL_STORAGE_VAR.APP_NEWS_SHOW_ONE_MONTH);
    const lastRunDate = lastRunDateStr ? new Date(lastRunDateStr) : null;
    const currentDate = new Date();

    if (!lastRunDate || this.isMonthPassedSineShowNewsPanel(lastRunDate, currentDate)) {
      localStorage.setItem(LOCAL_STORAGE_VAR.APP_NEWS_SHOW_ONE_MONTH, currentDate.toISOString());
      this.isVisibleNewsModule = true;
    }
  }

  private isMonthPassedSineShowNewsPanel(lastRunDate: Date, currentDate: Date): boolean {
    const oneMonthInMs = 30 * 24 * 60 * 60 * 1000;
    return (currentDate.getTime() - lastRunDate.getTime()) >= oneMonthInMs;
  }

}
